/**
 * Implements a glyph cache that will pack text glyphs into a set of texture atlases for rendering.
 */
import { FontAtlasPacker } from "./impl/FontAtlasPacker";
import { CanvasTexture, LinearFilter } from "three";
import { HarfBuzz } from "../../external/HarfBuzz";
import { SDFGenerator } from "../../external/SDFGenerator";
export class GlyphCache {
    /**
     *
     * @param font Reference to the font that the GlyphCache will be caching glyphs for
     * @param size The side length each atlas texture will have
     * @param glyphSize The width/height of each glyph in the glyph cache
     */
    constructor(font, size, glyphSize) {
        this.glyphs = new Map();
        this.textures = [];
        this.font = font;
        this.glyphSize = glyphSize;
        this.packer = new FontAtlasPacker(size, size);
    }
    get size() {
        return this.packer.width;
    }
    /**
     * Adds the given glyph into the cache, if it is not already present in the cache. Will return either the existing
     * glyph or a newly rendered one if it wasn't already cached.
     * @param glyphId
     */
    addGlyph(glyphId) {
        const fetchedGlyph = this.glyphs.get(glyphId);
        if (fetchedGlyph !== undefined) {
            return fetchedGlyph;
        }
        else {
            // Allocate space for a glyph in one of the backing textures
            const packResult = this.packer.pack([
                {
                    id: glyphId,
                    w: this.glyphSize,
                    h: this.glyphSize,
                },
            ])[0];
            // Check if we need to allocate a new font texture
            this.addTextureIfNeeded(packResult);
            // Convert the glyph into a path
            const pathString = this.font.glyphToPath(glyphId);
            const path = HarfBuzz.pathStringToObject(pathString);
            // Extract and bundle info about the glyph and insert it into our set of glyphs
            const { xMin, yMin, xMax, yMax } = calculatePathBounds(path);
            const fontUnitsMargin = (Math.max(xMax - xMin, yMax - yMin) / this.glyphSize) * (GlyphCache.SDF_MARGIN * this.glyphSize + 0.5);
            const glyph = Object.assign({ path: pathString, pathBounds: [xMin, yMin, xMax, yMax], sdfViewBox: [
                    xMin - fontUnitsMargin,
                    yMin - fontUnitsMargin,
                    xMax + fontUnitsMargin,
                    yMax + fontUnitsMargin,
                ] }, packResult);
            this.glyphs.set(glyphId, glyph);
            // Render the distance field into the glyph
            this.renderGlyphIntoCanvas(glyph);
            return glyph;
        }
    }
    /**
     * Returns the texture for the corresponding atlas index
     * @param i
     */
    getTexture(i) {
        return this.textures[i];
    }
    /**
     * Returns the number of backing atlas textures the atlas is currently using
     */
    get textureCount() {
        return this.textures.length;
    }
    /**
     * Internal function for adding new canvas textures to the atlas when needed
     * @param packResult
     * @private
     */
    addTextureIfNeeded(packResult) {
        if (packResult.atlasIndex >= this.textures.length) {
            // Construct a new canvas with a size equal to the texture packer's expected dimensions
            const ctx = document.createElement("canvas").getContext("webgl");
            ctx.canvas.width = this.packer.width;
            ctx.canvas.height = this.packer.height;
            this.textures.push(new CanvasTexture(ctx.canvas, undefined, undefined, undefined, LinearFilter, LinearFilter));
        }
    }
    /**
     * Internal function for rendering a glyph into the canvas
     * @param glyph
     * @private
     */
    renderGlyphIntoCanvas(glyph) {
        // Get the bounds of the glyph within the glyph path's virtual coordinate system. This should map from the
        // "local 2D space" of the path into the uniform sdfSize*sdfSize area we're generating the SDF into.
        const maxDistance = Math.max(glyph.sdfViewBox[2] - glyph.sdfViewBox[0], glyph.sdfViewBox[3] - glyph.sdfViewBox[1]);
        // Render the glyph into the atlas and mark for re-upload
        SDFGenerator.module.generateIntoCanvas(this.glyphSize, this.glyphSize, glyph.path, glyph.sdfViewBox, maxDistance, 9, this.textures[glyph.atlasIndex].image, glyph.x, glyph.y, 0);
        this.textures[glyph.atlasIndex].needsUpdate = true;
    }
}
GlyphCache.SDF_MARGIN = 1 / 16;
function calculatePathBounds(path) {
    let xMin;
    let yMin;
    let xMax;
    let yMax;
    if (path.length) {
        xMin = Infinity;
        yMin = Infinity;
        xMax = -Infinity;
        yMax = -Infinity;
        for (const seg of path) {
            switch (seg.type) {
                case "M":
                case "L":
                case "Q":
                case "C": {
                    const x = seg.values[0];
                    const y = seg.values[1];
                    xMin = Math.min(xMin, x);
                    yMin = Math.min(yMin, y);
                    xMax = Math.max(xMax, x);
                    yMax = Math.max(yMax, y);
                    break;
                }
            }
        }
    }
    else {
        xMin = xMax = yMin = yMax = 0;
    }
    return { xMin, yMin, xMax, yMax };
}
